export const personaList = [
  { id: '01', label: '正在探索的学生' },
  { id: '02', label: '刚开始工作的新人' },
  { id: '03', label: '独立创作者' },
  { id: '04', label: '社群 / 活动组织者' },
  { id: '05', label: '正在做个人项目的人' },
  { id: '06', label: '想重新开始的人' },
];

export const categories = [
  { id: 'all', label: '全部' },
  { id: 'learning', label: '学习方法' },
  { id: 'review', label: '项目复盘' },
  { id: 'career', label: '职场成长' },
  { id: 'creation', label: '内容创作' },
  { id: 'community', label: '社群活动' },
  { id: 'personal', label: '个人成长' },
  { id: 'life', label: '生活成长' },
  { id: 'other', label: '其他' },
];

export interface ExperienceCard {
  id: string;
  title: string;
  author: string;
  category: string;
  categoryId: string;
  description: string;
  tags: string[];
  boundaryTag: string;
  imageUrl: string;
  coverIndex: number;
  status: 'v2' | 'v1';
  cardType: 'gold' | 'ink';
  isDemo?: boolean;
}

type CardSeed = [
  title: string,
  description: string,
  tagOne: string,
  tagTwo: string,
  boundary: string,
];

const categoryMeta: Record<string, { label: string; author: string }> = {
  learning: { label: '学习方法', author: '正在重建学习系统' },
  review: { label: '项目复盘', author: '把踩坑写下来' },
  career: { label: '职场成长', author: '刚开始独立负责事情' },
  creation: { label: '内容创作', author: '持续公开创作' },
  community: { label: '社群活动', author: '在连接一群人' },
  personal: { label: '个人成长', author: '练习向前一步' },
  life: { label: '生活成长', author: '认真过普通生活' },
  other: { label: '其他', author: '把小事认真做完' },
};

const cardSeeds: Record<string, CardSeed[]> = {
  learning: [
    ['我如何用 AI 整理完半年的课堂笔记，考前不再崩溃', '笔记散落在备忘录、课本和截图里，我用一个固定流程把它们压缩成可复习的提纲。', '笔记分散', '考前抓重点', '不适用：已经有成熟笔记系统'],
    ['我如何每天只用 30 分钟，把英语听力从听不懂练到能跟读', '不追求一次听懂全部，而是固定使用一段材料完成盲听、查漏和跟读。', '碎片时间学习', '听力基础薄弱', '不适用：需要短期应试冲刺'],
    ['我如何用“讲给别人听”，发现自己其实没有学会', '把每周最难的概念录成三分钟语音，卡住的地方就是下一轮复习入口。', '理解不扎实', '费曼学习法', '不适用：纯记忆型内容'],
    ['我如何在考试前 14 天，排出一张真的能完成的复习表', '我删掉了平均分配时间的做法，改为按薄弱程度和分值安排每天任务。', '考试规划', '时间有限', '不适用：距离考试不足三天'],
    ['我如何不看教程，第一次靠官方文档完成一个小程序', '先写出最小目标，再只查完成下一步所需的那一页文档，避免陷入教程收藏。', '编程入门', '文档学习', '不适用：完全不了解基础语法'],
    ['我如何把一个容易鸽掉的学习小组，坚持运营了四周', '用固定时间、固定产出和缺席补交替代打卡，让成员知道每周到底要完成什么。', '学习小组', '同伴监督', '不适用：成员目标差异过大'],
    ['我如何清理 300 条收藏，搭出一个真正会回看的资料库', '先按“下一步行动”分类，再删掉没有明确使用场景的内容，只保留会被调用的资料。', '数字整理', '收藏太多', '不适用：资料有严格归档要求'],
    ['我如何读完一本难书后，还能在一个月后说出它的核心', '每章只写一个问题、一个观点和一个生活例子，读完后用五张卡复述整本书。', '深度阅读', '长期记忆', '不适用：需要逐字精读的专业教材'],
    ['我如何从“不敢问”，到每周向老师提出一个有效问题', '先写下我已经尝试了什么，再把问题缩小到一个判断点，降低开口的心理压力。', '主动提问', '课堂参与', '不适用：问题涉及隐私或敏感评价'],
    ['我如何用下班后的 90 分钟，三个月从零学会 UI 设计基础', '把每天任务固定成临摹、拆解和复盘三段，不再用“学设计”这种模糊目标要求自己。', '零基础技能', '下班后学习', '不适用：需要快速入职设计岗位'],
    ['我如何用错题里的三个信号，找到真正没掌握的知识点', '不按题号抄答案，而是标记看不懂、想错了和算错了三类原因，再决定复习顺序。', '错题复盘', '薄弱点定位', '不适用：题目本身存在争议'],
    ['我如何用一次公开分享，逼自己真正学懂一个新主题', '先定下十分钟分享，再倒推必须回答的三个问题，让学习从收藏资料变成可表达。', '输出式学习', '主题分享', '不适用：内容涉及保密信息'],
  ],
  review: [
    ['我如何在预算很低时办完一场校园活动', '把预算从物料清单改成体验优先级，先保证参与者真正会感受到的三个节点。', '预算受限', '校园活动', '不适用：已有稳定赞助商'],
    ['我如何把一次失败的项目，复盘成下一次成功的起点', '不再只写“沟通不足”，而是还原每个错误决定当时依据了什么信息。', '项目失败', '复盘习惯', '不适用：项目仍在紧急止损期'],
    ['我如何把延期两周的小组作业，重新拉回可交付状态', '把争论目标改成确认最小交付物，重新分配所有人能在 48 小时内完成的任务。', '团队延期', '重新排期', '不适用：成员已经完全失联'],
    ['我第一次参加黑客松，如何在 48 小时内砍掉一半功能', '先确定现场演示的唯一高光时刻，其余功能只保留支撑这个时刻的部分。', '黑客松', '功能取舍', '不适用：已有明确商业交付清单'],
    ['我如何复盘一个上线后几乎没有用户的个人项目', '找三位目标用户看着他们第一次使用，记录他们在哪一步失去兴趣。', '个人项目', '冷启动失败', '不适用：产品尚未达到可用状态'],
    ['我如何救回一场报名很少的工作坊', '停止继续投放海报，改为邀请十位真正会受益的人并询问不报名的原因。', '工作坊', '报名不足', '不适用：活动主题本身不可调整'],
    ['我如何把模糊的 AI 点子，三天做成一个能被试用的原型', '只做输入、处理和结果三个屏幕，用真实任务验证用户是否愿意继续。', 'AI 原型', '三天 MVP', '不适用：涉及高风险专业判断'],
    ['我如何解决志愿者排班总在最后一天崩掉的问题', '提前收集不可用时段，并给关键岗位设置一位明确的替补，而不是临时群里喊人。', '志愿者管理', '排班协作', '不适用：人员规模小于五人'],
    ['我如何复盘一次讲砸了的公开汇报', '分别记录内容、节奏和现场反应，只修改影响理解最大的一个问题。', '公开汇报', '表达复盘', '不适用：问题来自设备故障'],
    ['我如何用三天做出第一个能收反馈的 MVP', '把所有想法改写成一个用户动作，先让五个人完成它，再决定下一版。', '快速验证', '最小产品', '不适用：必须满足合规审核的产品'],
    ['我如何复盘一次合作中断，找到真正没有对齐的节点', '按承诺、理解和交付三个阶段还原对话，找到双方第一次出现不同预期的位置。', '合作复盘', '预期对齐', '不适用：纠纷已进入法律程序'],
    ['我如何把一场数据很差的活动，拆成三个还能保留的部分', '分别看触达、到场和参与，不用一个总结果否定所有已经有效的动作。', '活动数据', '保留有效部分', '不适用：数据采集本身不可靠'],
  ],
  career: [
    ['我如何用一封邮件，让陌生前辈愿意花时间看我的作品', '邮件只说明为什么找对方、我已经做了什么，以及希望得到哪一个具体判断。', '建立人脉', '作品反馈', '不适用：已有稳定导师关系'],
    ['我第一次和主管做 1 对 1，如何避免只汇报进度', '提前写下一个需要资源的问题和一个希望对方校准的判断，让谈话能推动事情。', '向上沟通', '一对一会议', '不适用：处于正式绩效申诉'],
    ['我如何用一张周报，平稳度过试用期前三个月', '周报不堆工作量，只说明目标、结果、风险和下周需要确认的事情。', '试用期', '工作透明', '不适用：团队明确不使用周报'],
    ['我如何在跨部门需求里，避免成为来回传话的人', '把口头要求整理成共同确认的问题、负责人和截止时间，再开始执行。', '跨部门协作', '需求澄清', '不适用：紧急事故处置阶段'],
    ['我如何第一次对不合理需求说“不”，又没有让合作中断', '不直接拒绝对方目标，而是说清资源限制并提供两个可执行替代方案。', '边界沟通', '需求管理', '不适用：涉及安全与合规红线'],
    ['我如何把作品集从“做了很多”，改成“我解决了什么”', '每个项目只保留背景、关键判断、取舍和结果，删掉无法说明能力的过程截图。', '求职作品集', '项目叙事', '不适用：岗位要求完整过程档案'],
    ['我如何用 90 天验证自己是否真的想转行', '每月完成一个真实小项目，并和三位从业者核对工作日常，而不是只买课程。', '职业转型', '低风险试错', '不适用：当前工作已影响身心安全'],
    ['我如何从开会不敢说话，到能在关键节点表达判断', '会前只准备一句结论和一条证据，在议题结束前主动说出来。', '会议表达', '新人职场', '不适用：议题涉及未授权信息'],
    ['我犯错后如何向团队解释，而不是急着证明自己', '先说明影响和补救动作，再补充原因，避免把道歉变成一场自我辩护。', '错误沟通', '承担责任', '不适用：需要法律或公关介入'],
    ['我如何写一份让接手同事少问一半问题的交接文档', '按任务触发场景整理入口、判断标准和常见故障，而不是罗列所有文件。', '工作交接', '文档沉淀', '不适用：工作内容仍高度变化'],
    ['我如何在资源不够时，向主管提出一个更容易被批准的方案', '把请求写成目标、最低资源和不做的代价，让对方能够直接做判断。', '争取资源', '方案表达', '不适用：预算已经完全冻结'],
    ['我如何第一次带实习生，没有把所有事情重新做一遍', '先约定交付标准和检查节点，把反馈放在关键判断上，而不是逐句修改。', '新人带教', '授权反馈', '不适用：任务涉及高风险权限'],
  ],
  creation: [
    ['我如何把 30 个读书笔记碎片，变成第一篇被转载的文章', '先找反复出现的问题，再用三个真实片段组织观点，不从空白标题开始写。', '碎片笔记', '文章结构', '不适用：学术论文写作'],
    ['我如何连续发 30 天短视频，没有在第七天放弃', '一次只固定一个内容模板，集中拍摄，再用数据决定下周修改哪一处。', '短视频更新', '持续创作', '不适用：需要每日追热点'],
    ['我如何从零开始写一封有 100 位订阅者的周报', '先服务身边十位具体的人，每周回答他们真正会问的一个问题。', '邮件周报', '早期读者', '不适用：已有成熟媒体渠道'],
    ['我如何给一篇普通内容，找到一个真正想点开的标题', '先写清读者正在面对的情境和可以得到的变化，再删掉空泛形容词。', '标题写作', '读者视角', '不适用：严肃新闻标题'],
    ['我如何把两小时访谈录音，剪成一篇有温度的人物故事', '不按时间顺序整理，而是围绕一个变化保留最能让读者理解人物的细节。', '人物采访', '内容剪辑', '不适用：需要完整口述史记录'],
    ['我如何用一张内容日历，结束每天临时想选题', '把内容分成解释、案例、幕后和互动四类，每周只在同一结构里换主题。', '内容规划', '选题焦虑', '不适用：突发新闻账号'],
    ['我如何在“还不够好”时，发出第一篇公开作品', '给首版设置明确截止时间，只检查是否说清楚，不在发布前追求完整体系。', '完美主义', '首次发布', '不适用：高风险专业建议'],
    ['我如何把 AI 当编辑，而不是让它替我写完', '先给出自己的事实和判断，只让 AI 提问、重组和指出读者可能不懂的地方。', 'AI 协作', '保留个人声音', '不适用：需要完全原创考试写作'],
    ['我如何用十条真实评论，改掉内容里最自嗨的部分', '把评论按看不懂、不相信和不知道怎么做分类，再逐类修改。', '用户反馈', '内容迭代', '不适用：评论样本明显失真'],
    ['我第一次接商业 brief，如何同时守住表达和交付', '先把品牌目标、不可改项和创作者判断写成一页确认，再开始制作。', '商业内容', '需求对齐', '不适用：尚未确认合同范围'],
    ['我如何把一场线上直播，整理成五条还能继续传播的内容', '按问题、关键判断和现场例子切分素材，让每个片段都能独立被理解。', '直播复用', '内容拆分', '不适用：直播内容存在授权限制'],
    ['我如何用同一个主题，写出三种不重复的内容角度', '分别从方法、失败和真实案例进入，保持核心判断一致但服务不同阅读情境。', '主题延展', '内容系列', '不适用：主题信息量不足'],
  ],
  community: [
    ['第一次社团招新：我如何从无人报名做到 42 人到场', '停止群发海报后，我改为邀请第一批关键参与者，让他们带来后续传播。', '社团招新', '冷启动', '不适用：已有成熟招新渠道'],
    ['我如何把一个没人说话的新群，带到第一次真实讨论', '不发欢迎词刷屏，而是先抛出一个所有成员都能用经历回答的问题。', '新群冷启动', '讨论设计', '不适用：公告型通知群'],
    ['我如何让活动志愿者不再只等负责人派活', '把任务墙、完成标准和求助入口公开，让每个人可以直接认领下一步。', '志愿者协作', '任务透明', '不适用：高风险专业岗位'],
    ['我如何办完第一次 20 人线下聚会，没有让现场冷掉', '提前设计三次自然换桌和一个共同任务，让交流不依赖主持人硬撑。', '线下聚会', '破冰设计', '不适用：正式商务会议'],
    ['我如何让新成员在加入第一周就找到参与感', '不是一次讲完所有规则，而是给新人一个当天可以完成的小贡献。', '新人融入', '参与设计', '不适用：需要长期培训的岗位'],
    ['我如何处理群里第一次公开冲突，没有急着站队', '先确认事实和影响，再把价值判断移出公共争论，分别沟通可接受的下一步。', '社群冲突', '边界维护', '不适用：存在骚扰或安全风险'],
    ['我如何和成员一起写出一份真的有人遵守的群规', '从过去发生过的三个具体问题出发，让规则对应行为和处理方式。', '社区规则', '共同制定', '不适用：法定制度文件'],
    ['我如何谈到第一个愿意互换资源的活动伙伴', '不先讲自己缺什么，而是说明双方受众的交集和一个最小合作实验。', '资源合作', '伙伴关系', '不适用：涉及排他商业合作'],
    ['我如何让活动反馈不再只有“挺好的”', '把问卷改成具体时刻、没发生的期待和下次是否愿意参与三个问题。', '活动复盘', '有效反馈', '不适用：需要标准满意度统计'],
    ['我如何把普通参与者，变成愿意一起完善活动的共创者', '活动结束前邀请他们选择一个想改进的小问题，并给出下一次参与入口。', '社区共创', '持续参与', '不适用：一次性封闭活动'],
    ['我如何让线上社群的第一次共创，不变成负责人独自收尾', '开始前就把决策范围、认领方式和完成标准公开，让贡献能够真正进入结果。', '线上共创', '责任认领', '不适用：结果必须由单一负责人签字'],
    ['我如何在活动结束后，让第一次来的人愿意留下来', '在热度消失前给出一个低门槛后续行动，而不是只发送合影和感谢。', '活动留存', '后续行动', '不适用：活动本身没有持续目标'],
  ],
  personal: [
    ['我如何克服公开表达前的拖延和恐惧', '把准备目标从“不能出错”改成“让一个人听懂”，先完成一次低风险试讲。', '公开表达', '准备拖延', '不适用：需要专业演讲治疗'],
    ['我如何在一次失败后，用七天重新开始行动', '不急着制定更大计划，每天只恢复一个能证明自己还在前进的小动作。', '失败恢复', '重新开始', '不适用：仍处于严重情绪危机'],
    ['我如何把拖了三个月的事情，在一个下午真正启动', '把“完成项目”改成一个 20 分钟能留下痕迹的动作，并提前移除开始阻力。', '拖延启动', '微行动', '不适用：任务依赖外部审批'],
    ['我如何第一次拒绝别人后，没有陷入整晚内疚', '先承认对方需求，再说清自己的限制，不用过度解释来换取理解。', '学会拒绝', '关系边界', '不适用：紧急安全求助'],
    ['我如何用每周 20 分钟复盘，不再被日子推着走', '只回答做成了什么、卡在哪里、下周停止什么三个问题。', '每周复盘', '自我管理', '不适用：需要医疗级情绪记录'],
    ['我如何面对“我不配”的冒牌者感，而不是继续假装自信', '把模糊的自我否定拆成可核对的能力证据和仍需学习的具体部分。', '冒牌者感', '能力证据', '不适用：持续影响日常功能'],
    ['我如何靠兑现小承诺，慢慢找回对自己的信任', '每天只承诺一件十分钟能完成的事，连续记录实际兑现，而不是追求强度。', '自我信任', '小承诺', '不适用：目标有明确硬截止'],
    ['我如何在陌生聚会里，不再逼自己变得外向', '只给自己一个任务：认识一个人并问完三个真正好奇的问题。', '社交焦虑', '陌生场合', '不适用：需要专业心理支持'],
    ['我如何让中断两个月的习惯重新开始，而不是从头惩罚自己', '从旧习惯里保留最容易恢复的触发点，把标准降到今天就能完成。', '习惯重启', '降低门槛', '不适用：习惯本身已经不再重要'],
    ['我如何第一次开口求助，又没有觉得自己很失败', '先说明我已经尝试了什么，再提出一个对方可以明确回答的小请求。', '主动求助', '具体请求', '不适用：涉及紧急专业救援'],
    ['我如何把持续的自我怀疑，改写成一个可以验证的问题', '不问“我是不是不行”，而是确认缺少哪项证据，再设计一次小规模尝试。', '自我怀疑', '行动验证', '不适用：长期影响基本生活'],
    ['我如何在计划被打乱后，十分钟重新找到当天的重点', '先放弃追回全部进度，只确认今天不做会造成真实影响的唯一事项。', '计划变化', '重新聚焦', '不适用：处于紧急事故现场'],
  ],
  life: [
    ['我如何用一张纸的规则，结束合租冰箱空间之战', '把模糊抱怨改成区域、过期处理和公共物品三条可执行约定。', '合租生活', '公共空间', '不适用：独自居住'],
    ['我如何用一份清单，完成第一次独立搬家没有漏掉重要手续', '按搬家前、当天和入住后一周拆分任务，并标出必须本人办理的事项。', '独立搬家', '生活清单', '不适用：跨国搬迁'],
    ['我如何用一小时备餐，让工作日不再天天点外卖', '固定两种主食、两种蛋白质和三种蔬菜，先解决组合而不是追求菜谱丰富。', '工作日饮食', '低成本备餐', '不适用：有特殊医疗饮食要求'],
    ['我如何从月光开始，攒下第一笔应急备用金', '发薪日先转走一个不会影响基本生活的小比例，再逐月调整。', '基础储蓄', '应急资金', '不适用：存在高息紧急债务'],
    ['我如何用“只留一个最好用的”，清掉堆满桌面的杂物', '每次只处理一个使用场景，并给保留物品设置明确位置。', '空间整理', '减少物品', '不适用：物品涉及收藏价值'],
    ['我如何把手机移出卧室，让睡前一小时真正安静下来', '用实体闹钟替代手机，并在客厅设置固定充电位置，减少意志力消耗。', '睡眠习惯', '减少屏幕', '不适用：夜间需要紧急值班'],
    ['我如何和伴侣重新分配家务，不再靠“谁看不下去谁做”', '列出家务的完整责任而不只是动作，让每项事情有明确负责人。', '共同生活', '家务协作', '不适用：存在控制或安全问题'],
    ['我如何用周日晚 30 分钟，让工作日少一点手忙脚乱', '只确认固定安排、三件重要事情和一个可以提前准备的阻力点。', '每周计划', '生活节奏', '不适用：工作排班高度随机'],
    ['我第一次独自旅行，如何在不排满行程的情况下获得安全感', '每天只固定住宿、交通和一个核心地点，其余时间留给现场判断。', '独自旅行', '弹性计划', '不适用：高风险目的地'],
    ['我如何让家里的宠物喂养不再靠群聊里反复确认', '用一张共享记录表标记时间、份量和异常情况，所有人只看同一个入口。', '宠物照护', '家庭协作', '不适用：需要专业医疗监测'],
    ['我如何把每月账单整理成一张五分钟就能看懂的表', '只保留固定支出、可调整支出和异常变化，让复盘直接对应下个月行动。', '生活记账', '支出复盘', '不适用：企业财务管理'],
    ['我如何用一个周末，把长期拖延的房间维修真正完成', '先列出需要工具、需要预约和自己能做的部分，按依赖关系一次处理。', '家庭维修', '任务拆解', '不适用：涉及电路和结构安全'],
  ],
  other: [
    ['我如何第一次摆摊，把没人停留改成愿意开口询问', '把所有介绍压缩成一个一眼能懂的体验动作，并主动记录路人停下来的原因。', '线下摆摊', '快速沟通', '不适用：需要专业销售许可'],
    ['我如何在 24 小时内，为临时展览做出一套可用导视', '先保证入口、方向和出口三个关键判断，再统一字体、颜色和编号。', '临时展览', '导视设计', '不适用：大型公共安全场所'],
    ['我如何把一次志愿经历，整理成后来者能直接接手的手册', '不写流水账，只保留现场判断、常见意外和联系人入口。', '志愿服务', '经验交接', '不适用：涉及受助者隐私'],
    ['我如何第一次采访陌生人，让对话不只停在标准答案', '先从一个具体经历开始追问，再确认细节而不是急着进入下一题。', '陌生人采访', '深度提问', '不适用：敏感调查采访'],
    ['我如何给第一次作品展，找到五位真正愿意反馈的观众', '不广泛求评价，而是按内容、呈现和理解分别邀请不同背景的人。', '作品展示', '定向反馈', '不适用：作品尚未形成可观看版本'],
    ['我如何组织一次旧物交换，让现场没有变成杂物堆', '提前限制品类和数量，用明确标签说明状态，并设置无人领取的处理方式。', '旧物交换', '现场秩序', '不适用：涉及贵重物品交易'],
    ['我如何用一张表，协调四个人完成一次短途拍摄', '把地点、镜头、负责人和备选方案放在同一时间线上，减少现场口头确认。', '短片拍摄', '多人协作', '不适用：大型专业剧组'],
    ['我如何第一次做公开工作坊，让参与者真的带走一个结果', '把内容从“听懂”改成现场完成一个作品，并为每一步设置可见产出。', '公开工作坊', '现场产出', '不适用：纯理论讲座'],
    ['我如何把一次失败的比赛经历，写成队伍下一年的训练计划', '按准备、现场判断和协作拆开原因，把每个问题转成可重复训练的动作。', '比赛复盘', '训练计划', '不适用：比赛规则已经完全变化'],
    ['我如何第一次向陌生店主谈合作，没有一上来就被拒绝', '先说明能带来的具体客流和最小试行方式，让合作不需要一次做大决定。', '本地合作', '小规模试行', '不适用：需要独家授权的合作'],
    ['我如何把朋友间的旅行计划，从群聊争论变成三个可选方案', '先收集每个人不能妥协的条件，再只比较预算、时间和体验差异。', '多人旅行', '共同决策', '不适用：存在紧急签证问题'],
    ['我如何给一个没人负责的小任务，建立清楚的结束标准', '先明确谁确认完成、结果放在哪里，以及什么情况下需要重新打开。', '临时任务', '完成标准', '不适用：职责已有正式制度规定'],
  ],
};

export const experienceCards: ExperienceCard[] = Object.entries(cardSeeds).flatMap(
  ([categoryId, seeds], categoryIndex) => {
    const meta = categoryMeta[categoryId];
    return seeds.map((seed, index) => ({
      id: `${categoryId}-${String(index + 1).padStart(2, '0')}`,
      title: seed[0],
      author: `@${meta.author}的人 / 示例`,
      category: meta.label,
      categoryId,
      description: seed[1],
      tags: [seed[2], seed[3]],
      boundaryTag: seed[4],
      imageUrl: '/experience-cover-sprite-v1.webp',
      coverIndex: (categoryIndex * 2 + index) % 9,
      status: index % 3 === 0 ? 'v2' : 'v1',
      cardType: index % 2 === 0 ? 'gold' : 'ink',
    }));
  },
);

export const flowSteps = [
  { id: '01', label: '真实经历', icon: 'ri-file-text-line' },
  { id: '02', label: 'AI 编辑', icon: 'ri-robot-line' },
  { id: '03', label: '作者确认', icon: 'ri-check-double-line' },
  { id: '04', label: '他人试用', icon: 'ri-user-shared-line' },
  { id: '05', label: '反馈更新', icon: 'ri-loop-left-line' },
];
